import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Email, Lock, Visibility, VisibilityOff } from '@mui/icons-material';

/* ── Design tokens ─────────────────────────────────────────────── */
const PRIMARY = '#6C98AD';
const PRIMARY_HOVER = '#5a8499';
const SECONDARY = '#24272a';
const CARD_SHADOW = '0 4px 20px rgba(108, 152, 173, 0.15)';
const CARD_SHADOW_HOVER = '0 8px 30px rgba(108, 152, 173, 0.25)';
const FONT_HEADING = "'Avenir Next LT Pro', sans-serif";
const FONT_BODY = "'Avenir Next LT Pro', sans-serif";
const RADIUS = '1rem';
const TRANSITION = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const from = location.state?.from?.pathname || '/admin';

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return 'Email is required';
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const validatePassword = (password) => {
    if (!password) {
      return 'Password is required';
    }
    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    return '';
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (emailError) {
      setEmailError(validateEmail(value));
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    if (passwordError) {
      setPasswordError(validatePassword(value));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate all fields
    const emailErr = validateEmail(email);
    const passErr = validatePassword(password);
    
    setEmailError(emailErr);
    setPasswordError(passErr);

    if (emailErr || passErr) {
      return;
    }

    setLoading(true);
    try {
      await login(email.toLowerCase().trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: `url(${process.env.PUBLIC_URL}/static/images/mstructure_login_bg.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        fontFamily: FONT_BODY,
        position: 'relative',
        overflow: 'hidden',
        /* Dark overlay for better readability */
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          pointerEvents: 'none',
        },
      }}
    >
      <Paper
        component="form"
        onSubmit={handleSubmit}
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          width: '100%',
          maxWidth: 440,
          borderRadius: RADIUS,
          boxShadow: CARD_SHADOW,
          border: '2px solid rgba(108, 152, 173, 0.15)',
          transition: TRANSITION,
          position: 'relative',
          zIndex: 1,
          backgroundColor: '#ffffff',
          '&:hover': {
            boxShadow: CARD_SHADOW_HOVER,
            borderColor: PRIMARY,
          },
        }}
      >
        {/* Logo + heading */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <img
            src={process.env.PUBLIC_URL + '/TALOGOWEBSITE.webp'}
            alt="Tailored Alloys"
            height="40"
            style={{ marginBottom: 16 }}
          />
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: SECONDARY, fontFamily: FONT_HEADING }}
          >
            Admin Panel
          </Typography>
          <Typography variant="body2" sx={{ color: PRIMARY, mt: 0.5, fontFamily: FONT_BODY }}>
            Sign in to manage your dashboard
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: RADIUS }}>
            {error}
          </Alert>
        )}

        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={handleEmailChange}
          onBlur={() => setEmailError(validateEmail(email))}
          fullWidth
          required
          error={!!emailError}
          helperText={emailError}
          sx={{
            mb: 2.5,
            '& .MuiOutlinedInput-root': {
              borderRadius: RADIUS,
              fontFamily: FONT_BODY,
              '&.Mui-focused fieldset': { borderColor: PRIMARY },
            },
            '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email sx={{ color: PRIMARY }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={handlePasswordChange}
          onBlur={() => setPasswordError(validatePassword(password))}
          fullWidth
          required
          error={!!passwordError}
          helperText={passwordError}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              borderRadius: RADIUS,
              fontFamily: FONT_BODY,
              '&.Mui-focused fieldset': { borderColor: PRIMARY },
            },
            '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock sx={{ color: PRIMARY }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  sx={{ color: PRIMARY }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loading}
          sx={{
            py: 1.4,
            borderRadius: RADIUS,
            fontWeight: 600,
            fontSize: '1rem',
            fontFamily: FONT_BODY,
            textTransform: 'none',
            background: `linear-gradient(135deg, ${PRIMARY} 0%, #8eb4c7 100%)`,
            boxShadow: '0 4px 12px rgba(108, 152, 173, 0.3)',
            transition: TRANSITION,
            '&:hover': {
              background: `linear-gradient(135deg, ${PRIMARY_HOVER} 0%, ${PRIMARY} 100%)`,
              boxShadow: '0 8px 20px rgba(108, 152, 173, 0.5)',
              transform: 'translateY(-2px)',
            },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
        </Button>

        <Box sx={{ textAlign: 'center', mt: 2.5 }}>
          <Link
            to="/admin/forgot-password"
            style={{
              color: PRIMARY,
              textDecoration: 'none',
              fontFamily: FONT_BODY,
              fontSize: '0.9rem',
            }}
          >
            Forgot password?
          </Link>
        </Box>
      </Paper>
    </Box>
  );
}
